import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Authentication context
import { useAuth } from "context/AuthContext";
import API_BASE_URL from "config/api";

function UserManagement() {
  const { getToken } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistFilter, setWaitlistFilter] = useState("all");
  const [autoApprove, setAutoApprove] = useState(false);
  const [manualEmail, setManualEmail] = useState("");
  const [sns, setSns] = useState({ open: false, message: "", color: "success" });

  const API_URL = API_BASE_URL;

  const fetchData = async () => {
    setLoading(true);
    const token = getToken();
    try {
      const [usersRes, waitlistRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/waitlist`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const [usersData, waitlistData, settingsData] = await Promise.all([
        usersRes.json(),
        waitlistRes.json(),
        settingsRes.json(),
      ]);

      if (usersData.success) setUsers(usersData.data);
      if (waitlistData.success) setWaitlist(waitlistData.data);
      if (settingsData.success) {
        setAutoApprove(!!settingsData.data.autoApprove);
      }
    } catch (error) {
      setSns({ open: true, message: "Failed to fetch data", color: "error" });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);
  const handleSetWaitlistFilter = (event, newValue) => setWaitlistFilter(newValue);

  const handleApprove = async (email) => {
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/admin/waitlist/approve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.success) {
        setSns({ open: true, message: `Approved ${email}`, color: "success" });
        fetchData();
      }
    } catch (error) {
      setSns({ open: true, message: "Approval failed", color: "error" });
    }
  };

  const handleDisapprove = async (email) => {
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/admin/waitlist/disapprove`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.success) {
        setSns({
          open: true,
          message: `Disapproved ${email}`,
          color: "warning",
        });
        fetchData();
      }
    } catch (error) {
      setSns({ open: true, message: "Disapproval failed", color: "error" });
    }
  };

  const handleToggleAutoApprove = async (event) => {
    const isChecked = event.target.checked;
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/admin/settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ settings: { autoApprove: isChecked } }),
      });
      const data = await res.json();
      if (data.success) {
        setAutoApprove(isChecked);
        setSns({
          open: true,
          message: `Auto-approve ${isChecked ? "enabled" : "disabled"}`,
          color: "success",
        });
      }
    } catch (error) {
      setSns({ open: true, message: "Failed to update settings", color: "error" });
    }
  };

  const handleAddWaitlist = async () => {
    if (!manualEmail) return;
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/admin/waitlist/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: manualEmail }),
      });
      const data = await res.json();
      if (data.success) {
        setSns({ open: true, message: "Added to waitlist", color: "success" });
        setManualEmail("");
        fetchData();
      }
    } catch (error) {
      setSns({ open: true, message: "Failed to add", color: "error" });
    }
  };

  const userColumns = [
    { Header: "name", accessor: "name", width: "25%" },
    { Header: "email", accessor: "email", width: "35%" },
    { Header: "joined", accessor: "createdAt", width: "20%" },
    { Header: "action", accessor: "action", width: "20%" },
  ];

  const waitlistColumns = [
    { Header: "email", accessor: "email", width: "40%" },
    { Header: "status", accessor: "status", width: "20%" },
    { Header: "action", accessor: "action", width: "40%" },
  ];

  const userRows = users.map((u) => ({
    name: u.profile_info?.name || u.username || "N/A",
    email: u.email,
    createdAt: new Date(u.createdAt).toLocaleDateString(),
    action: (
      <MDButton variant="text" color="error" onClick={() => handleDisapprove(u.email)}>
        Disapprove
      </MDButton>
    ),
  }));

  const filteredWaitlist = waitlist.filter((w) => {
    if (waitlistFilter === "all") return true;
    if (waitlistFilter === "approved") return w.approved;
    if (waitlistFilter === "pending") return !w.approved;
    return true;
  });

  const waitlistRows = filteredWaitlist.map((w) => ({
    email: w.email,
    status: (
      <MDTypography
        variant="caption"
        color={w.approved ? "success" : "warning"}
        fontWeight="medium"
      >
        {w.approved ? "Approved" : "Pending"}
      </MDTypography>
    ),
    action: (
      <MDBox display="flex">
        {!w.approved ? (
          <MDButton variant="text" color="info" onClick={() => handleApprove(w.email)}>
            Approve
          </MDButton>
        ) : (
          <MDButton variant="text" color="error" onClick={() => handleDisapprove(w.email)}>
            Disapprove
          </MDButton>
        )}
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={4}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="h5">Management Console</MDTypography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDBox display="flex" justifyContent="center">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={autoApprove}
                            onChange={handleToggleAutoApprove}
                            color="info"
                          />
                        }
                        label={
                          <MDTypography variant="button" fontWeight="medium">
                            Auto-Approve New Users
                          </MDTypography>
                        }
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDBox display="flex" justifyContent="flex-end">
                      <MDInput
                        label="Add email to waitlist"
                        value={manualEmail}
                        onChange={(e) => setManualEmail(e.target.value)}
                        sx={{ mr: 2 }}
                      />
                      <MDButton variant="gradient" color="info" onClick={handleAddWaitlist}>
                        Add
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox px={2}>
                <AppBar position="static">
                  <Tabs
                    value={tabValue}
                    onChange={handleSetTabValue}
                    sx={{ background: "transparent" }}
                  >
                    <Tab label="Registered Users" icon={<Icon>people</Icon>} />
                    <Tab label="Waitlist" icon={<Icon>hourglass_empty</Icon>} />
                  </Tabs>
                </AppBar>
              </MDBox>
              <MDBox p={3}>
                {tabValue === 1 && (
                  <MDBox mb={3}>
                    <AppBar position="static">
                      <Tabs
                        value={waitlistFilter}
                        onChange={handleSetWaitlistFilter}
                        sx={{ background: "transparent" }}
                      >
                        <Tab label="All" value="all" />
                        <Tab label="Approved" value="approved" />
                        <Tab label="Pending" value="pending" />
                      </Tabs>
                    </AppBar>
                  </MDBox>
                )}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <MDBox>
                    {tabValue === 0 ? (
                      <DataTable
                        table={{ columns: userColumns, rows: userRows }}
                        isSorted={false}
                        entriesPerPage={false}
                        showTotalEntries={false}
                        noEndBorder
                      />
                    ) : (
                      <DataTable
                        table={{ columns: waitlistColumns, rows: waitlistRows }}
                        isSorted={false}
                        entriesPerPage={false}
                        showTotalEntries={false}
                        noEndBorder
                      />
                    )}
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <MDSnackbar
        color={sns.color}
        icon="check"
        title="Admin Notification"
        content={sns.message || "Notification"}
        dateTime="now"
        open={sns.open}
        onClose={() => setSns({ ...sns, open: false })}
        close={() => setSns({ ...sns, open: false })}
      />
    </DashboardLayout>
  );
}

export default UserManagement;
